// Shared derivation for the per-row status of a bulk landlord-research run, used by
// both /leads/search (LeadSearchPage) and /leads (LeadsPage) so the two surfaces agree.
//
// The bulk worker researches the launch-ordered ref list ONE row at a time, bumping the
// operation's progress.current as each row finishes. So a row's index against `current`
// tells you where it stands: rows before it are done, the row AT it is being researched
// now, rows after it are queued. progress.processed carries the ACTIVE row's live search
// count. This is driven by the single bulk-op poll every surface already runs - there is
// no per-row polling (see building-manager-api operation_landlord_research_bulk.py).

// Every landlord-research operation is filed under one fixed site id - mirrors
// LEADS_RESEARCH_SITE_ID in lambda_handlers/long_ops/operation_landlord_research.py.
// Needed to poll a run this tab did not launch: the row carries the operation key,
// but no site id (a lead is not a site, and neither is a VOA row).
export const LEADS_RESEARCH_SITE_ID = "leads-research";

// How many live research runs a leads surface watches at once through their own
// operation rows (`useOperations`). One small read per run per tick; the width is a
// decision, not the number of rows carrying a key. Runs past the cap are still drawn
// "researching" off their row and are picked up on the next list read - a wait, never
// a wrong state - and single-row runs each cost the operator a paid click, so the cap
// is generous for what the page actually sees.
export const RESEARCH_OP_POLL_WIDTH = 8;
// A research run takes minutes, so watching one is about noticing it ENDED; the
// panel's own poll of the open row (which shows the live search count) is what
// runs at the faster default cadence, and the two share a query.
export const RESEARCH_OP_POLL_INTERVAL_MS = 8000;

// The operation type of a SINGLE-subject research run. A bulk op's progress counts
// whichever row that batch is working, so its count must never be shown on another
// row of the batch - hence the type gate where a search count is read.
export const SINGLE_RESEARCH_OPERATION_TYPE = "landlord_research";

// The research operations a surface should watch: every key its rows carry, minus the
// batch it already polls as a whole (that op refreshes its own rows as the worker
// advances), plus any run this tab launched whose key has not come back on a row yet.
// Keyed by operation key, because a bulk run from another tab stamps one key on many
// rows and must be polled once, not once per row.
export const liveResearchOperations = <T>(
  rows: T[],
  rowRef: (row: T) => string,
  rowOperationKey: (row: T) => string | null,
  ownBatchRefs: ReadonlySet<string>,
  launched: ReadonlyMap<string, string>
): { operationKeys: string[]; refsByOperationKey: Map<string, string[]> } => {
  const refsByOperationKey = new Map<string, string[]>();
  const add = (key: string, ref: string) => {
    const refs = refsByOperationKey.get(key);
    if (refs) {
      if (!refs.includes(ref)) refs.push(ref);
    } else {
      refsByOperationKey.set(key, [ref]);
    }
  };
  for (const row of rows) {
    const key = rowOperationKey(row);
    const ref = rowRef(row);
    if (key && !ownBatchRefs.has(ref)) add(key, ref);
  }
  launched.forEach((key, ref) => add(key, ref));
  return {
    operationKeys: Array.from(refsByOperationKey.keys()).slice(
      0,
      RESEARCH_OP_POLL_WIDTH
    ),
    refsByOperationKey,
  };
};

export type BulkResearchRowState =
  | "done"
  | "researching"
  | "queued"
  | "cancelled"
  | null;

// `null` = this ref is not part of the in-flight bulk batch (or there is none).
// `cancelledRefs` is the operator's optimistic set of rows pulled from the queue via
// the per-row Cancel: those override the index math so a cancelled row stops showing
// queued/researching (and doesn't flip to "done" once the worker skips past it).
export function bulkResearchRowStateForRef(
  ref: string,
  refs: string[] | null | undefined,
  current: number,
  cancelledRefs?: ReadonlySet<string> | null
): BulkResearchRowState {
  if (!refs) return null;
  const idx = refs.indexOf(ref);
  if (idx < 0) return null;
  if (cancelledRefs?.has(ref)) return "cancelled";
  if (idx < current) return "done";
  if (idx === current) return "researching";
  return "queued";
}

// The single chip a left-list row shows for its research state. Only these four
// (plus null) render; the bulk run's "done"/"cancelled" carry no visible chip.
export type ResearchRowChip = "researching" | "queued" | "not-run" | null;

// Combine every research signal a row can carry into ONE chip state, so
// /leads/search and /leads render the research chip identically (the lead
// pipeline's "surfaces that must agree" rule). It layers on top of
// bulkResearchRowStateForRef:
//   - a single-row "Research landlord" this page has just launched for this row,
//   - the in-flight bulk sub-batch (done/researching/queued),
//   - rows selected into the auto-continue chain that are waiting for a LATER
//     batch (the current sub-batch's overflow) -> still "queued",
//   - rows a finished chain never reached (auto-continue's no-progress guard
//     tripped, or a hard stop) -> a persistent "not-run" chip.
export function researchRowChipState(params: {
  ref: string;
  // Single-row research runs this page has just launched, whose rows have not yet
  // come back carrying their server key. A SET, not one ref: single-row runs go in
  // parallel, so a second launch must not un-assert the first (see
  // useLiveRuns).
  singleResearchingRefs?: ReadonlySet<string> | null;
  // The in-flight bulk sub-batch: the queued refs, the worker's cursor, and the
  // rows the operator pulled from the queue.
  bulkRefs?: string[] | null;
  bulkCurrent?: number;
  bulkCancelledRefs?: ReadonlySet<string> | null;
  // Rows in the active chain that are waiting for a later auto-continue batch.
  chainPendingRefs?: ReadonlySet<string> | null;
  // Rows a finished chain never reached - the "not run - run again" markers.
  notRunRefs?: ReadonlySet<string> | null;
  // The row's OWN `researchOperationKey` from the server: a live research op covers
  // it right now. Every signal above is the launching page's in-memory handle, which
  // is lost by navigating away, searching another area, or opening a second tab -
  // this one travels with the row, so a row is never drawn idle mid-run.
  serverOperationKey?: string | null;
}): ResearchRowChip {
  const {
    ref,
    singleResearchingRefs,
    bulkRefs,
    bulkCurrent = 0,
    bulkCancelledRefs,
    chainPendingRefs,
    notRunRefs,
    serverOperationKey,
  } = params;
  // Active single-row work always wins over a persisted "not run" marker.
  if (singleResearchingRefs?.has(ref)) return "researching";
  const bulk = bulkResearchRowStateForRef(
    ref,
    bulkRefs,
    bulkCurrent,
    bulkCancelledRefs
  );
  if (bulk === "researching") return "researching";
  if (bulk === "queued") return "queued";
  // "done"/"cancelled" render nothing; only a ref that isn't in the current
  // sub-batch at all (bulk === null) can be a future-chain or unreached row.
  if (bulk === null) {
    // Local "queued" is MORE specific than the server key (which only says a run
    // covers this row), so it wins where we have it.
    if (chainPendingRefs?.has(ref)) return "queued";
    // A live server-side run beats a stale "not run" marker from an earlier chain.
    if (serverOperationKey) return "researching";
    if (notRunRefs?.has(ref)) return "not-run";
  }
  return null;
}

// The shape of a completed bulk-research operation's `result` payload (from the
// BE worker in operation_landlord_research_bulk.py). Only the fields the FE
// continuation logic reads are typed.
export interface BulkResearchResultShape {
  status?: string;
  researched?: string[];
  researchedCount?: number;
  failed?: { sourceRef?: string }[];
  notProcessed?: string[];
}

export interface BulkContinuationPlan {
  // "continue" -> re-launch research for `remaining`; "stop" -> flag `remaining`
  // as "not run" and surface the tail; "none" -> clean finish, nothing to do.
  action: "continue" | "stop" | "none";
  // Rows still needing research (to re-launch on "continue", or flag on "stop").
  remaining: string[];
  // Hard failures in THIS batch (the caller accumulates across the chain).
  batchFailedCount: number;
}

// Decide what a completed bulk-research sub-batch should do next. Shared by
// /leads/search and /leads so the two surfaces can never drift (the completion
// logic used to be copy-pasted into both).
//
// `inputRefs` is what THIS batch was asked to research. The worker caps its own
// input to BULK_MAX_ROWS and only reports the wall-clock tail of the capped batch
// in `notProcessed` - it never reports the rows selected past the cap. So "what's
// left" is derived from what actually got DONE (researched + hard-failed), never
// from `notProcessed`; deriving it from notProcessed would silently drop every
// selected row past the cap. The strict-shrink guard (remaining < inputRefs) is
// the no-progress stop: a batch that finished nothing never re-launches, so the
// chain can't loop forever.
export function planBulkResearchContinuation(
  result: BulkResearchResultShape,
  inputRefs: string[],
  cancelledRefs: ReadonlySet<string>
): BulkContinuationPlan {
  const batchFailedCount = Array.isArray(result.failed)
    ? result.failed.length
    : 0;
  // `researched` is the authoritative "these finished" list. If it is somehow
  // absent (a BE too old to return it - not possible with the paired backend,
  // but guard anyway), we can't tell which rows finished, so fall back to the
  // conservative `notProcessed` set rather than mis-flagging every researched
  // row as "not run".
  const researchedList = Array.isArray(result.researched)
    ? result.researched
    : null;
  let remaining: string[];
  if (researchedList) {
    const done = new Set<string>(researchedList.filter(Boolean));
    (result.failed ?? []).forEach((f) => {
      if (f?.sourceRef) done.add(f.sourceRef);
    });
    remaining = inputRefs.filter((r) => !done.has(r) && !cancelledRefs.has(r));
  } else {
    remaining = (
      Array.isArray(result.notProcessed) ? result.notProcessed : []
    ).filter((r) => !cancelledRefs.has(r));
  }
  if (remaining.length > 0 && remaining.length < inputRefs.length) {
    return { action: "continue", remaining, batchFailedCount };
  }
  if (remaining.length > 0 || batchFailedCount > 0) {
    return { action: "stop", remaining, batchFailedCount };
  }
  return { action: "none", remaining: [], batchFailedCount };
}
